import { useState, useEffect } from 'react';
import { IconAlertCircleFilled } from '@tabler/icons-react';
import { ensureMinLoading } from '../Utils/withLoading';
import { apiFetch } from '../api/client';

const FONT = '"Public Sans", sans-serif';

const inputClass =
  'w-full outline-none bg-white/[0.08] focus:bg-white/[0.12] focus:shadow-[inset_0_0_0_1px_rgba(255,255,255,0.16)] placeholder:text-white/30 transition-all duration-200 ease-out';

const inputStyle = {
  border: 'none',
  borderRadius: '16px',
  height: '48px',
  padding: '0 16px',
  fontSize: '15px',
  fontWeight: 500,
  fontFamily: FONT,
  color: 'rgba(255, 255, 255, 0.95)',
  width: '100%',
  boxSizing: 'border-box',
};

const inputDisabledStyle = {
  ...inputStyle,
  backgroundColor: 'rgba(255, 255, 255, 0.025)',
  color: 'rgba(255, 255, 255, 0.35)',
  cursor: 'not-allowed',
};

const inputErrorStyle = {
  ...inputStyle,
  boxShadow: 'inset 0 0 0 1px rgba(255, 69, 58, 0.55)',
};

const labelStyle = {
  fontSize: '13px',
  lineHeight: '18px',
  fontWeight: 600,
  color: 'rgba(255, 255, 255, 0.45)',
  fontFamily: FONT,
};

const textareaStyle = {
  ...inputStyle,
  borderRadius: '18px',
  height: 'auto',
  padding: '14px 16px',
  resize: 'vertical',
  minHeight: '84px',
};

const units = ['piezas', 'gramos', 'ml', 'oz'];

function safeFloat(val) {
  if (val === '' || val === null || val === undefined) return undefined;
  const n = parseFloat(val);
  return isNaN(n) ? undefined : n;
}

function toDateString(value) {
  if (!value) return '';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  return '';
}

export default function ModalEditarItemInventario({ isOpen, onClose, onSaved, item, notify }) {
  const [form, setForm] = useState({
    name: '',
    description: '',
    sku: '',
    unit_of_measure: 'piezas',
    current_quantity: '',
    minimum_stock: '',
    maximum_stock: '',
    cost_per_unit: '',
    supplier: '',
    location: '',
    expiration_date: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (item && isOpen) {
      setForm({
        name: item.name || '',
        description: item.description || '',
        sku: item.sku || '',
        unit_of_measure: item.unit_of_measure || 'piezas',
        current_quantity: item.current_quantity != null ? String(item.current_quantity) : '',
        minimum_stock: item.minimum_stock != null ? String(item.minimum_stock) : '',
        maximum_stock: item.maximum_stock != null ? String(item.maximum_stock) : '',
        cost_per_unit: item.cost_per_unit != null ? String(item.cost_per_unit) : '',
        supplier: item.supplier || '',
        location: item.location || '',
        expiration_date: toDateString(item.expiration_date),
        notes: item.notes || '',
      });
      setSaving(false);
      setError('');
    }
  }, [item, isOpen]);

  if (!isOpen || !item) return null;

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (error) setError('');
  };

  const handleMaximumChange = (value) => {
    handleChange('maximum_stock', value);
    const max = parseFloat(value);
    if (!isNaN(max) && max > 0) {
      handleChange('minimum_stock', String(Math.round(max * 0.15 * 100) / 100));
    } else {
      handleChange('minimum_stock', '');
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError('El nombre es obligatorio.');
      return;
    }

    setSaving(true);
    setError('');
    const startedAt = Date.now();
    notify?.('loading', 'Actualizando insumo...');

    try {
      const body = {};
      if (form.name.trim()) body.name = form.name.trim();
      if (form.description.trim()) body.description = form.description.trim();
      if (form.sku.trim()) body.sku = form.sku.trim();
      if (form.unit_of_measure) body.unit_of_measure = form.unit_of_measure;

      const qty = safeFloat(form.current_quantity);
      const min = safeFloat(form.minimum_stock);
      const max = safeFloat(form.maximum_stock);
      const cost = safeFloat(form.cost_per_unit);

      if (qty !== undefined) body.current_quantity = qty;
      if (min !== undefined) body.minimum_stock = min;
      if (max !== undefined) body.maximum_stock = max;
      if (cost !== undefined) body.cost_per_unit = cost;

      if (form.supplier.trim()) body.supplier = form.supplier.trim();
      if (form.location.trim()) body.location = form.location.trim();
      if (form.expiration_date) body.expiration_date = form.expiration_date;
      if (form.notes.trim()) body.notes = form.notes.trim();

      const res = await apiFetch(`/inventory/${item.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        let msg = 'Algo ha salido mal. Si el problema persiste, por favor reporta el error.';
        try {
          const err = await res.json();
          if (err.detail) msg = err.detail;
          else if (Array.isArray(err.detail)) msg = err.detail.map(d => d.msg).join(', ');
        } catch {}
        if (res.status === 409 || (res.status === 500 && msg.toLowerCase().includes('unique'))) {
          msg = 'Ya existe un insumo con ese SKU.';
        } else if (res.status >= 500) {
          msg = 'Algo ha salido mal. Si el problema persiste, por favor reporta el error.';
        }
        notify?.('clear');
        setError(msg);
        return;
      }

      await ensureMinLoading(startedAt);
      notify?.('clear');
      onSaved?.();
      onClose();
    } catch (e) {
      notify?.('clear');
      setError('Algo ha salido mal. Si el problema persiste, por favor reporta el error.');
    } finally {
      setSaving(false);
    }
  };

  const canSave = form.name.trim() && !saving;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3"
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.72)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="editar-insumo-titulo"
        className="flex flex-col overflow-hidden w-full"
        style={{
          backgroundColor: '#000',
          borderRadius: '28px',
          border: '1px solid rgba(255, 255, 255, 0.16)',
          maxWidth: '880px',
          maxHeight: 'min(760px, calc(100dvh - 24px))',
          boxShadow: '0 40px 120px rgba(0, 0, 0, 0.8)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="shrink-0 px-5 sm:px-6 pt-5 sm:pt-6 pb-4 border-b border-white/[0.16]">
          <h2
            id="editar-insumo-titulo"
            className="font-semibold m-0 truncate"
            style={{
              fontSize: '22px',
              lineHeight: '28px',
              letterSpacing: '-0.02em',
              color: 'rgba(255, 255, 255, 0.95)',
              fontFamily: FONT,
            }}
          >
            Editar insumo
          </h2>
          <p
            className="m-0 mt-1 truncate"
            style={{
              fontSize: '13px',
              lineHeight: '18px',
              fontWeight: 500,
              color: 'rgba(255, 255, 255, 0.45)',
              fontFamily: FONT,
            }}
          >
            {item.name}{form.sku ? ` · ${form.sku}` : ''}
          </p>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto pedidos-scroll px-4 sm:px-6 py-5">
          {error && (
            <div
              role="alert"
              className="flex items-start gap-2.5 rounded-2xl p-3.5 mb-5"
              style={{ backgroundColor: 'rgba(255, 69, 58, 0.08)' }}
            >
              <IconAlertCircleFilled size={18} color="#ff453a" style={{ flexShrink: 0, marginTop: '1px' }} />
              <span
                style={{
                  fontSize: '13px',
                  lineHeight: '19px',
                  fontWeight: 500,
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontFamily: FONT,
                }}
              >
                {error}
              </span>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
            <div className="flex flex-col gap-5">
              <div className="flex flex-col gap-2">
                <label htmlFor="editar-insumo-nombre" style={labelStyle}>Nombre *</label>
                <input
                  id="editar-insumo-nombre"
                  type="text"
                  value={form.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="Ej: Café en grano, Leche entera"
                  className={inputClass}
                  style={!form.name.trim() && error ? inputErrorStyle : inputStyle}
                />
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="editar-insumo-sku" style={labelStyle}>SKU</label>
                <input
                  id="editar-insumo-sku"
                  type="text"
                  value={form.sku}
                  disabled
                  className="w-full"
                  style={inputDisabledStyle}
                />
              </div>

              <div className="flex flex-col gap-2">
                <span style={labelStyle}>Unidad de medida</span>
                <div className="flex flex-wrap gap-2">
                  {units.map((u) => {
                    const selected = form.unit_of_measure === u;
                    return (
                      <button
                        key={u}
                        type="button"
                        onClick={() => handleChange('unit_of_measure', u)}
                        aria-pressed={selected}
                        className={`flex items-center justify-center h-10 px-4 rounded-full border-none cursor-pointer touch-manipulation select-none transition-colors duration-200 ease-out ${
                          selected ? '' : '[@media(hover:hover)]:hover:bg-white/[0.1]'
                        }`}
                        style={{
                          backgroundColor: selected ? '#fff' : 'rgba(255, 255, 255, 0.06)',
                          fontSize: '13px',
                          fontWeight: 600,
                          fontFamily: FONT,
                          color: selected ? '#000' : 'rgba(255, 255, 255, 0.75)',
                        }}
                      >
                        {u}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="editar-insumo-proveedor" style={labelStyle}>Proveedor</label>
                <input
                  id="editar-insumo-proveedor"
                  type="text"
                  value={form.supplier}
                  onChange={(e) => handleChange('supplier', e.target.value)}
                  placeholder="Ej: Café Premium MX"
                  className={inputClass}
                  style={inputStyle}
                />
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="editar-insumo-descripcion" style={labelStyle}>Descripción</label>
                <textarea
                  id="editar-insumo-descripcion"
                  value={form.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="Describe el insumo..."
                  rows={2}
                  className={`${inputClass} resize-y`}
                  style={textareaStyle}
                />
              </div>
            </div>

            <div className="flex flex-col gap-5">
              <div className="flex flex-col gap-2">
                <label htmlFor="editar-insumo-cantidad" style={labelStyle}>Cantidad actual</label>
                <input
                  id="editar-insumo-cantidad"
                  type="number"
                  min="0"
                  step="0.001"
                  value={form.current_quantity}
                  onChange={(e) => handleChange('current_quantity', e.target.value)}
                  placeholder="0"
                  className={inputClass}
                  style={inputStyle}
                />
                <span
                  style={{
                    fontSize: '12px',
                    lineHeight: '16px',
                    fontWeight: 500,
                    color: 'rgba(255, 255, 255, 0.35)',
                    fontFamily: FONT,
                  }}
                >
                  Ajusta aquí el stock disponible en tu almacén.
                </span>
              </div>

              <div className="flex gap-4">
                <div className="flex flex-col gap-2 flex-1" style={{ minWidth: 0 }}>
                  <label htmlFor="editar-insumo-minimo" style={labelStyle}>Stock mínimo</label>
                  <input
                    id="editar-insumo-minimo"
                    type="number"
                    min="0"
                    step="0.001"
                    value={form.minimum_stock}
                    onChange={(e) => handleChange('minimum_stock', e.target.value)}
                    placeholder="0"
                    className={inputClass}
                    style={inputStyle}
                  />
                </div>
                <div className="flex flex-col gap-2 flex-1" style={{ minWidth: 0 }}>
                  <label htmlFor="editar-insumo-maximo" style={labelStyle}>Stock máximo</label>
                  <input
                    id="editar-insumo-maximo"
                    type="number"
                    min="0"
                    step="0.001"
                    value={form.maximum_stock}
                    onChange={(e) => handleMaximumChange(e.target.value)}
                    placeholder="0"
                    className={inputClass}
                    style={inputStyle}
                  />
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="editar-insumo-costo" style={labelStyle}>Costo por unidad</label>
                <input
                  id="editar-insumo-costo"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.cost_per_unit}
                  onChange={(e) => handleChange('cost_per_unit', e.target.value)}
                  placeholder="$0.00"
                  className={inputClass}
                  style={inputStyle}
                />
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="editar-insumo-ubicacion" style={labelStyle}>Ubicación</label>
                <input
                  id="editar-insumo-ubicacion"
                  type="text"
                  value={form.location}
                  onChange={(e) => handleChange('location', e.target.value)}
                  placeholder="Ej: Almacén A"
                  className={inputClass}
                  style={inputStyle}
                />
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="editar-insumo-caducidad" style={labelStyle}>Fecha de caducidad</label>
                <input
                  id="editar-insumo-caducidad"
                  type="date"
                  value={form.expiration_date}
                  onChange={(e) => handleChange('expiration_date', e.target.value)}
                  className={inputClass}
                  style={{ ...inputStyle, colorScheme: 'dark' }}
                />
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="editar-insumo-notas" style={labelStyle}>Notas</label>
                <textarea
                  id="editar-insumo-notas"
                  value={form.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  placeholder="Notas adicionales..."
                  rows={2}
                  className={`${inputClass} resize-y`}
                  style={textareaStyle}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:justify-end gap-2 shrink-0 px-4 sm:px-6 py-4 border-t border-white/[0.16]">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="flex items-center justify-center h-12 px-6 rounded-full border-none cursor-pointer touch-manipulation select-none transition-colors duration-200 ease-out active:scale-[0.98] [@media(hover:hover)]:hover:bg-white/[0.85]"
            style={{
              backgroundColor: '#fff',
              fontSize: '14px',
              fontWeight: 600,
              letterSpacing: '-0.01em',
              color: '#000',
              fontFamily: FONT,
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.5 : 1,
            }}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className="flex items-center justify-center h-12 px-7 rounded-full border-none cursor-pointer touch-manipulation select-none transition-colors duration-200 ease-out active:scale-[0.98] [@media(hover:hover)]:hover:bg-[#0077ed]"
            style={{
              backgroundColor: canSave ? '#0071e3' : 'rgba(255, 255, 255, 0.06)',
              fontSize: '15px',
              fontWeight: 600,
              letterSpacing: '-0.01em',
              fontFamily: FONT,
              color: canSave ? '#fff' : 'rgba(255, 255, 255, 0.35)',
              cursor: canSave ? 'pointer' : 'not-allowed',
            }}
          >
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}
